import { Router } from "aurelia-router";
import { BindingEngine, computedFrom } from "aurelia-binding";
import { BaseValidationResponse } from "../../../models/validations/validations.models";
import { UnitOfWorkObserver, UnitOfWorkItem } from "../../../utilities/unit-of-work-observer";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from '../../../services/basics/notification-client.service';
import { EventAggregator } from "aurelia-event-aggregator";
import { StepModel } from "../../../models/basics/basics.models";
import { EntityTypes, IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { activationStrategy, RouteConfig } from "aurelia-router";
import { Guid } from "../../../utilities/guid";
import { ValidationsClientService } from '../../../services/validations/validations-client.service';
import { NotifyPropertyChangedBase, Reflection } from '../../../utilities/reflection';

export abstract class BaseEntity<T extends IEntityTypeIdentifier> extends NotifyPropertyChangedBase {

    private _item: T;

    protected _routeConfig: RouteConfig;
    protected _params;
    protected unitOfWorkObserver: UnitOfWorkObserver<T>;
    public isCreateScreen: boolean;
    public saving: boolean;
    public idPropertyName: string = "id";
    public steps: StepModel[] = [];
    public handlingUpdateCompleted: boolean = false;
    public generatedId: number;

    @computedFrom(Reflection.getPropertyPath<BaseEntity<T>>(z => z._item))
    public get isNew(): boolean {
        return !(this.getItemIdInternal() && this.getItemIdInternal() > 0);
    }

    @computedFrom(Reflection.getPropertyPath<BaseEntity<T>>(z => z._item))
    public get hasSavePermission(): boolean {
        return true;
    }

    @computedFrom(Reflection.getPropertyPath<BaseEntity<T>>(z => z._item))
    public get hasDeletePermission(): boolean {
        return true;
    }

    @computedFrom(Reflection.getPropertyPath<BaseEntity<T>>(z => z._item))
    public get item(): T {
        return this._item;
    }

    public set item(x: T) {
        this._item = x;
        this.onItemChanged();
    }

    constructor(protected router: Router,
        protected bindingEngine: BindingEngine,
        protected i18N: I18N,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected validationsClientService: ValidationsClientService) {
        super();
    }

    protected onItemChanged() { }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    protected dialogClose() { }

    public goToList() {
        if (this._params.isFromDialog) {
            this.dialogClose();
        } else {
            this.router.navigateToRoute(this.getListRoute());
        }
    }

    public goBack() {
        this.router.navigateBack();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected initializeItemOverride(newItem: T) {

    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected initializeItemOverrideForUpdate(newItem: T) {
    }

    public activate(params: any, routeConfig: RouteConfig): Promise<void> {
        // this is done like this because of this bug
        // 4489 Individual - cannot create a new individual if i have an individual already opened
        this._params = params;
        this._routeConfig = routeConfig;
        this.onActivation();
        const id: number = Number(params[this.idPropertyName]);
        return this.getEntity(id);
    }

    protected onActivation() {

    }

    public getEntity(id: number) {
        this.steps = [];
        return new Promise<void>((resolve, reject) => {
            this.getEntityInternal(id, (x) => {
                resolve(x);
            }, reject);
        });
    }

    protected getEntityInternal(id: number, resolve: any, reject: any) {
        if (id && id > 0) {
            this.getExistingEntity(id, resolve, reject);
        } else {
            this.isCreateScreen = true;
            const possiblePromiseItem = this.initializeItemForCreate();
            const func = (item) => {
                item.identifier = Guid.generateGuid();
                this.item = item;
                this.clearChanges(() => {
                    this.initializeItemOverride(this.item);
                    this.refreshSteps().then(() => {
                        resolve();
                    });
                });
            }
            if (possiblePromiseItem instanceof Promise) {
                possiblePromiseItem.then(x => {
                    func(x);
                }).catch(() => {
                    func(null);
                });
            } else {
                func(possiblePromiseItem);
            }
        }
    }

    protected getExistingEntity(id: number, resolve: any, reject: any) {
        this.isCreateScreen = false;

        this.getItem(Number(id))
            .then((response: T) => {
                this.item = response;
                this.refreshSteps().then(() => {
                    this.clearChanges(() => {
                        this.initializeItemOverrideForUpdate(this.item);
                        resolve();
                    });
                }).catch(() => { reject(); });
            }).catch(() => { reject(); });
    }

    protected stepsInitialized() {

    }

    public refreshSteps() {
        return new Promise<void>((resolve) => {                           
            this.steps = [];

            setTimeout(() => {
                const getStepsResponse = this.getSteps();
                if (getStepsResponse instanceof Promise) { 
                    getStepsResponse.then(steps => {
                        this.steps = steps;
                        this.stepsInitialized();
                        resolve();
                    });
                } else {
                    this.steps = getStepsResponse;
                    this.stepsInitialized();
                    resolve();
                }
            });            
        })
    }

    protected getPropertiesToSkip() {
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected arrayChangesInterceptor(arrayName: string, items: any[], added: boolean) {
        return items;
    }

    public async canDeactivate(): Promise<boolean> {
        return true;
    }

    public attached() {
        if (!this.isCreateScreen && this.unitOfWorkObserver) {
            this.unitOfWorkObserver.startTracking();
        }
    }

    public detached() {
        if (this.unitOfWorkObserver) {
            this.unitOfWorkObserver.dispose();
        }
    }

    protected hasChanges() {
        return this.unitOfWorkObserver.hasChanges();
    }

    protected getChanges(): { [index: string]: UnitOfWorkItem } {
        this.registerIEntityIdentifierChanges(this.item, null);
        return this.unitOfWorkObserver.getChanges();
    }

    private registerIEntityIdentifierChanges(item, preKey: string) {
        const propsToSkip = this.getPropertiesToSkip();

        Object.keys(item).forEach(key => {

            if (propsToSkip.some(x => key == x)) {
                return;
            }

            if (key == "identifier") {
                this.unitOfWorkObserver.addChange((preKey != null ? (preKey + ".identifier") : "identifier"), item.identifier, null);
            }

            const keyValue = item[key];
            if (keyValue != null &&
                typeof item[key] == "object" &&
                !(keyValue instanceof Date)) {
                if (!(keyValue instanceof Array)) {
                    if (!this.getPropertiesToSkip().some(c => c == key)) {
                        this.registerIEntityIdentifierChanges(keyValue, (preKey != null ? (preKey + "." + key) : key));
                    }
                } else {
                    keyValue.filter(x => x.id > 0).forEach((itx) => {
                        this.registerIEntityIdentifierChanges(itx, (preKey != null ? (preKey + "." + key) : key) + "." + itx.id);
                    });
                }
            }
        });
    }

    public clearChanges(callback: () => void = null) {
        if (this.unitOfWorkObserver) {
            this.unitOfWorkObserver.clearChanges();
            this.unitOfWorkObserver.dispose();
        }
        setTimeout(() => {
            this.unitOfWorkObserver = new UnitOfWorkObserver(this.bindingEngine, this.item, this.getPropertiesToSkip(), this.arrayChangesInterceptor);
            this.unitOfWorkObserver.startTracking();
            if (callback)
                callback();
        });
    }

    protected beforeSave(skipValidation: boolean, resolve: (isValid: boolean) => void) {
        resolve(true);
    }

    public save = (item: T, dontHandleOnComplete: boolean = false, skipValidation: boolean = false, returnNullWhenSaveError: boolean = false): Promise<() => void> => {

        return new Promise<() => void>((resolve) => {
            this.beforeSave(skipValidation, (isValid: boolean) => {
                setTimeout(() => { // set timeout so that the before save will be recorder in uow

                    if (!isValid) {
                        resolve(null);
                        return;
                    }

                    if (this.hasChanges()) {
                        this.saving = true;
                        const isNew = !(this.getItemIdInternal() && this.getItemIdInternal() > 0);
                        this.saveItem(this.getItemIdInternal(), this.getChanges()).then((response: BaseValidationResponse) => {
                            if (!skipValidation && response.validationResult && response.validationResult.hasError) {
                                this.validationsClientService.showValidationErrors(response.validationResult);
                                if (returnNullWhenSaveError) {
                                    this.saving = false;
                                    resolve(null);
                                    return;
                                }
                            }
                            if (!response.validationResult ||
                                (response.validationResult && !response.validationResult.hasError) ||
                                !response.shouldRollbackChangesOnValidationError) {
                                if (response.generatedId) {
                                    this.generatedId = response.generatedId;
                                }
                                if (!dontHandleOnComplete) {
                                    this.handleUpdateCompleted(response, isNew);
                                }
                            }
                            this.saving = false;
                            resolve(() => {
                                if (!dontHandleOnComplete)  
                                    this.handleUpdateCompleted(response, isNew);
                            });
                        }).catch((response) => {
                            if (response) {
                                if (response.validationResult && response.validationResult.hasError) {
                                    this.validationsClientService.showValidationErrors(response.validationResult);
                                } else if (response.name == "CustomError") {
                                    this.notificationService.show("Oops Error!", response.message, NotificationType.Error);
                                } else if (!(response.content && response.content.exception)) {
                                    this.notificationService.show("Oops Error!", this.i18N.tr("error-updating-text"), NotificationType.Error);
                                }
                            }
                            this.saving = false;
                            resolve(null);
                        });
                    } else {
                        this.notificationService.show("No Changes", this.i18N.tr("there-were-no-changes-to-save-text"), NotificationType.Info);
                        resolve(null);
                    }
                });
            });
        });
    }

    public getDurationSuccessMessage() {
        return 5000;
    }

    protected handleUpdateCompleted(response: any, isNew: boolean) {
        this.handlingUpdateCompleted = true;

        const title = this.i18N.tr(isNew ? "created-successfully-text" : "updated-successfully-text");

        const description = response.successMessage ? response.successMessage : "";

        this.notificationService.show(title, description, NotificationType.Success, this.getDurationSuccessMessage());

        if (!this._params[this.idPropertyName] || this._params[this.idPropertyName] <= 0) {
            this.generatedId = response.generatedId;
            this.clearChanges();
            if (this._params.isFromDialog) {
                this.dialogClose();
            } else {
                this.navigateToNew(response.generatedId ? response.generatedId : response.id);
                this.handlingUpdateCompleted = true;
            }
        } else {
            this.getEntity(this.getItemIdInternal()).then(() => {
                this.handlingUpdateCompleted = false;
            })
        }
    }

    public navigateToNew(id: number) {
        this.router.navigateToRoute(this.getUpdateRoute(), { id: id }, { replace: true });
    }

    protected getItemIdInternal() {
        return this.generatedId && this.generatedId > 0 ? this.generatedId : (this.getItemId() ? this.getItemId() : 0);
    }

    protected abstract getListRoute(): string;

    protected abstract getSteps(): StepModel[] | Promise<StepModel[]>;

    protected abstract getItem(id: number): Promise<T>;

    protected abstract getItemId(): number;

    protected abstract saveItem(id: number,
        changes: { [index: string]: UnitOfWorkItem }): Promise<BaseValidationResponse>;

    protected abstract getUpdateRoute(): string;

    protected abstract getEntityType(): EntityTypes;

    protected abstract initializeItemForCreate(): T | Promise<T>;

    setItem() { }
}
